import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const CLIENTE_COLOR = "#ed6c02";
const DATE_COLOR = "#ed6c02";
const ENVIOS_COUNT_COLOR = "#33ceb5";

const isBranch = (item) => Array.isArray(item?.items);

const firstLeafId = (node) => {
  if (!node) return null;
  if (isBranch(node)) {
    for (const child of node.items) {
      const id = firstLeafId(child);
      if (id) return id;
    }
    return null;
  }
  const id = Number(node.id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const findSelectedPath = (groups, selectedNum) => {
  for (const group of groups || []) {
    for (const item of group.items || []) {
      if (isBranch(item)) {
        const hit = (item.items || []).some(
          (leaf) => Number(leaf?.id) === selectedNum,
        );
        if (hit) {
          return { groupKey: group.key, branchKey: item.key };
        }
      } else if (Number(item?.id) === selectedNum) {
        return { groupKey: group.key, branchKey: null };
      }
    }
  }
  return { groupKey: null, branchKey: null };
};

function TreeLeafRow({
  item,
  selected,
  onSelect,
  closedLabel,
  openLabel,
  countTooltip,
  theme,
}) {
  const id = Number(item?.id);
  const closed = Boolean(item?.closed);
  const statusColor = closed
    ? theme.palette.text.secondary
    : theme.palette.success.main;
  const statusLabel = closed ? closedLabel : openLabel;
  const count =
    item?.count != null && item.count !== "" ? item.count : null;

  return (
    <Box
      onClick={() => onSelect?.(id)}
      data-tree-role="hoja"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.7,
        px: 0.85,
        py: 0.45,
        borderRadius: 1.2,
        cursor: "pointer",
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.14)
          : "transparent",
        border: "1px solid",
        borderColor: selected
          ? alpha(theme.palette.primary.main, 0.28)
          : "transparent",
        "&:hover": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            selected ? 0.16 : 0.06,
          ),
        },
      }}
    >
      {statusLabel ? (
        <Tooltip title={statusLabel}>
          <Box
            aria-label={statusLabel}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: statusColor,
            }}
          />
        </Tooltip>
      ) : (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: statusColor,
          }}
        />
      )}
      <Typography
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "0.76rem",
          fontWeight: selected ? 700 : 500,
          lineHeight: 1.25,
          color: selected ? "#455a64" : "#607080",
        }}
      >
        {item.label}
      </Typography>
      {item.date ? (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            flexShrink: 0,
            fontSize: "0.68rem",
            letterSpacing: 0.1,
            color: DATE_COLOR,
          }}
        >
          {item.date}
        </Typography>
      ) : null}
      {count != null ? (
        <Tooltip
          title={
            typeof countTooltip === "function"
              ? countTooltip(count)
              : countTooltip || ""
          }
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              flexShrink: 0,
              color: ENVIOS_COUNT_COLOR,
            }}
          >
            {count}
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
}

const ClientesNavTree = ({
  groups = [],
  selectedId,
  onSelect,
  title,
  countTooltip,
  branchCountTooltip,
  closedLabel,
  openLabel,
}) => {
  const theme = useTheme();
  const selectedNum = Number(selectedId);
  const selectedPath = useMemo(
    () => findSelectedPath(groups, selectedNum),
    [groups, selectedNum],
  );

  const [openKeys, setOpenKeys] = useState(() => new Set());

  useEffect(() => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (groups.length <= 8) {
        groups.forEach((g) => next.add(g.key));
      } else if (selectedPath.groupKey) {
        next.add(selectedPath.groupKey);
      }
      if (selectedPath.branchKey) next.add(selectedPath.branchKey);
      groups.forEach((g) => {
        const branches = (g.items || []).filter(isBranch);
        if (next.has(g.key) && branches.length === 1) {
          next.add(branches[0].key);
        }
      });
      return next;
    });
  }, [groups, selectedPath.groupKey, selectedPath.branchKey]);

  const toggleGroup = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectNode = (node, extraOpenKey) => {
    const firstId = firstLeafId(node);
    if (Number.isFinite(firstId) && firstId > 0) onSelect?.(firstId);
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (node?.key) next.add(node.key);
      if (extraOpenKey) next.add(extraOpenKey);
      return next;
    });
  };

  const branchTooltip = branchCountTooltip || countTooltip;

  return (
    <Paper
      variant="outlined"
      sx={{
        height: { xs: 220, md: "100%" },
        minHeight: { md: 0 },
        overflow: "auto",
        borderRadius: 2.5,
        p: 1,
      }}
    >
      {title ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            px: 0.75,
            pb: 0.75,
            color: "text.secondary",
            fontWeight: 800,
            letterSpacing: 0.35,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Stack spacing={0.25}>
        {groups.map((group) => {
          const open = openKeys.has(group.key);
          const accent = group.accent || CLIENTE_COLOR;
          const badge = String(group.badge || "").trim();
          return (
            <Box key={group.key}>
              <Box
                onClick={() => selectNode(group)}
                data-tree-role="cliente"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.55,
                  py: 0.45,
                  borderRadius: 1.2,
                  cursor: "pointer",
                  backgroundColor: alpha(accent, 0.07),
                  border: "1px solid",
                  borderColor: alpha(accent, 0.2),
                  "&:hover": {
                    backgroundColor: alpha(accent, 0.12),
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{ p: 0.15, color: accent }}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleGroup(group.key);
                  }}
                >
                  {open ? (
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    color: accent,
                    letterSpacing: 0.12,
                  }}
                >
                  {group.label}
                </Typography>
                {badge ? (
                  <Chip
                    size="small"
                    label={badge}
                    sx={{
                      height: 18,
                      flexShrink: 0,
                      fontWeight: 700,
                      fontSize: "0.58rem",
                      letterSpacing: 0.25,
                      color: accent,
                      backgroundColor: alpha(accent, 0.12),
                      border: "1px solid",
                      borderColor: alpha(accent, 0.32),
                      "& .MuiChip-label": { px: 0.65 },
                    }}
                  />
                ) : null}
              </Box>
              <Collapse in={open} unmountOnExit>
                <Stack spacing={0.2} sx={{ pl: 1.6, pr: 0.4, pb: 0.4, pt: 0.25 }}>
                  {(group.items || []).map((item) => {
                    if (isBranch(item)) {
                      const branchOpen = openKeys.has(item.key);
                      const branchCount =
                        item?.count != null && item.count !== ""
                          ? item.count
                          : (item.items || []).length;
                      return (
                        <Box key={item.key}>
                          <Box
                            onClick={() => selectNode(item, group.key)}
                            data-tree-role="articulo"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              px: 0.45,
                              py: 0.3,
                              borderRadius: 1,
                              cursor: "pointer",
                              backgroundColor: "transparent",
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.text.primary,
                                  0.04,
                                ),
                              },
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{ p: 0.1, color: "text.disabled" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleGroup(item.key);
                              }}
                            >
                              {branchOpen ? (
                                <ExpandMoreIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ChevronRightIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                            <Typography
                              noWrap
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: 800,
                                fontSize: "0.74rem",
                                color: "#111111",
                                letterSpacing: 0.08,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Tooltip
                              title={
                                typeof branchTooltip === "function"
                                  ? branchTooltip(branchCount)
                                  : branchTooltip || ""
                              }
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  color: "text.disabled",
                                }}
                              >
                                {branchCount}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Collapse in={branchOpen} unmountOnExit>
                            <Stack
                              spacing={0.15}
                              sx={{ pl: 1.8, pr: 0.2, pb: 0.25, pt: 0.15 }}
                            >
                              {(item.items || []).map((leaf) => {
                                const id = Number(leaf?.id);
                                return (
                                  <TreeLeafRow
                                    key={id}
                                    item={leaf}
                                    selected={id === selectedNum}
                                    onSelect={onSelect}
                                    closedLabel={closedLabel}
                                    openLabel={openLabel}
                                    countTooltip={countTooltip}
                                    theme={theme}
                                  />
                                );
                              })}
                            </Stack>
                          </Collapse>
                        </Box>
                      );
                    }

                    const id = Number(item?.id);
                    return (
                      <TreeLeafRow
                        key={id}
                        item={item}
                        selected={id === selectedNum}
                        onSelect={onSelect}
                        closedLabel={closedLabel}
                        openLabel={openLabel}
                        countTooltip={countTooltip}
                        theme={theme}
                      />
                    );
                  })}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default ClientesNavTree;
